
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path: string) => location.pathname === path;

  const menuItems = [
    { path: '/', label: 'DJT Mechanic', icon: 'ri-home-line' },
    { path: '/services', label: 'EV Services', icon: 'ri-tools-line' },
    { path: '/about', label: 'About Us', icon: 'ri-information-line' },
    { path: '/contact', label: 'Contact', icon: 'ri-phone-line' }
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all duration-700 relative overflow-visible ${
      isScrolled 
        ? 'bg-white/98 backdrop-blur-lg shadow-2xl border-b border-emerald-100/50' 
        : 'bg-gradient-to-br from-white via-emerald-50/80 to-teal-50/80'
    }`}>
      
      {/* Enhanced Background with Better Contrast */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-emerald-50/80 to-teal-50/80 z-0"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-green-50/50 to-cyan-50/40 z-0"></div>
      
      {/* Subtle Mesh Gradient Overlay */}
      <div className="absolute inset-0 opacity-40 z-0" style={{
        background: `radial-gradient(circle at 20% 30%, rgba(16, 185, 129, 0.12) 0%, transparent 50%),
                     radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
                     radial-gradient(circle at 40% 80%, rgba(20, 184, 166, 0.15) 0%, transparent 50%)`
      }}></div>
      
      {/* Enhanced Floating Background Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-5">
        <div className="absolute top-6 left-1/5 w-8 h-8 bg-gradient-to-br from-emerald-400/50 to-teal-500/50 rounded-full animate-float opacity-70 shadow-xl" style={{ animationDelay: '0s', animationDuration: '8s' }}></div>
        <div className="absolute top-5 right-1/4 w-7 h-7 bg-gradient-to-br from-cyan-400/50 to-blue-500/50 rounded-full animate-float opacity-70 shadow-lg" style={{ animationDelay: '2s', animationDuration: '9s' }}></div>
        <div className="absolute bottom-6 left-1/3 w-7 h-7 bg-gradient-to-br from-teal-400/50 to-emerald-500/50 rounded-full animate-float opacity-70 shadow-lg" style={{ animationDelay: '4s', animationDuration: '7s' }}></div>
        <div className="absolute top-7 right-1/6 w-8 h-8 bg-gradient-to-br from-green-300/50 to-emerald-400/50 rounded-full animate-float opacity-70 shadow-xl" style={{ animationDelay: '1s', animationDuration: '8.5s' }}></div>
      </div>
      
      <nav className="relative px-4 sm:px-6 lg:px-8 z-40">
        <div className="flex justify-between items-center h-20">
          {/* Enhanced Logo Section */}
          <div className="flex-shrink-0 flex items-center gap-3 group z-50">
            <Link to="/" className="flex items-center relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-cyan-400/20 rounded-full opacity-0 group-hover:opacity-100 blur-3xl transition-all duration-500 scale-150"></div>
              
              <img 
                src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/0cc9a7db9fcd45639685b8ea89aad17d.png" 
                alt="DJT EV Mechanic Logo" 
                className="h-14 w-auto relative z-10 transform group-hover:scale-110 transition-all duration-500 filter drop-shadow-2xl"
              />
            </Link>
            
            <div className="relative group">
              <img 
                src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/9bd8a6a42fcb951dc5e017b66725ccaf.png" 
                alt="EV Service Icon" 
                className="h-10 w-auto transform hover:scale-125 hover:rotate-6 transition-all duration-500 filter drop-shadow-xl relative z-10 animate-pulse"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/30 to-teal-400/30 rounded-full opacity-0 group-hover:opacity-100 blur-xl transition-all duration-500 scale-150"></div>
            </div>
          </div>

          {/* Enhanced Desktop Navigation with Larger Dynamic Icons */}
          <div className="hidden md:block z-50">
            <div className="ml-10 flex items-baseline space-x-2">
              {menuItems.map((item) => (
                <Link 
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 text-base font-semibold transition-all duration-300 group whitespace-nowrap rounded-lg shadow-sm hover:shadow-md border cursor-pointer ${
                    isActive(item.path)
                      ? 'text-white bg-gradient-to-r from-emerald-600 to-teal-600 border-emerald-500 shadow-lg scale-105'
                      : 'text-gray-800 hover:text-emerald-600 bg-white/80 hover:bg-white border-emerald-100/50 hover:border-emerald-300'
                  }`}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <i className={`${item.icon} text-2xl transform transition-all duration-300 ${
                      isActive(item.path) 
                        ? 'animate-bounce scale-110' 
                        : 'group-hover:scale-125 group-hover:rotate-12 group-hover:text-emerald-600'
                    }`}></i>
                    <span>{item.label}</span>
                  </span>
                  
                  {!isActive(item.path) && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg opacity-0 group-hover:opacity-100 transform scale-95 group-hover:scale-100 transition-all duration-300"></div>
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-emerald-600 to-teal-600 group-hover:w-4/5 transition-all duration-300 rounded-full"></div>
                    </>
                  )}
                  
                  {isActive(item.path) && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer rounded-lg"></div>
                  )}
                </Link>
              ))}
              
              {/* Enhanced CTA Button with Larger Icon */}
              <Link 
                to="/appointment" 
                className={`relative px-5 py-2 rounded-lg text-base font-semibold transition-all duration-300 group whitespace-nowrap cursor-pointer overflow-hidden transform hover:scale-105 shadow-md hover:shadow-lg border ${
                  isActive('/appointment')
                    ? 'bg-gradient-to-r from-cyan-700 via-teal-700 to-emerald-700 text-white border-cyan-600 scale-105 shadow-xl'
                    : 'bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white border-white/30'
                }`}
              >
                <span className="relative z-10 group-hover:text-white transition-colors duration-300 flex items-center gap-2">
                  <i className={`ri-calendar-line text-2xl transform transition-all duration-300 ${
                    isActive('/appointment') 
                      ? 'animate-heartbeat scale-110' 
                      : 'group-hover:scale-125 group-hover:rotate-12'
                  }`}></i>
                  <span>Book Service</span>
                </span>
                
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <div className="absolute inset-0 rounded-lg border border-white/40 scale-100 group-hover:scale-110 opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
              </Link>
            </div>
          </div>

          {/* Enhanced Mobile menu button with Larger Icon */}
          <div className="md:hidden z-50">
            <button
              onClick={toggleMenu}
              className="relative inline-flex items-center justify-center p-3 rounded-lg text-gray-900 hover:text-emerald-600 hover:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500 cursor-pointer transition-all duration-300 group transform hover:scale-105 shadow-sm hover:shadow-md bg-white/80 border border-emerald-100/50"
            >
              <div className="w-8 h-8 flex items-center justify-center relative">
                <div className="relative w-6 h-6">
                  <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ${
                    isMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'
                  }`}></span>
                  <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ${
                    isMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}></span>
                  <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ${
                    isMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'
                  }`}></span>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Enhanced Mobile Navigation with Larger Dynamic Icons */}
        <div className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out relative z-60 ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white/95 backdrop-blur-lg border-t border-emerald-100 relative shadow-xl rounded-b-2xl">
            {menuItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path}
                className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all duration-300 transform hover:translate-x-2 shadow-sm hover:shadow-md cursor-pointer ${
                  isActive(item.path)
                    ? 'text-white bg-gradient-to-r from-emerald-600 to-teal-600 scale-105 shadow-lg'
                    : 'text-gray-900 hover:text-emerald-600 hover:bg-emerald-50/80 bg-white/60 hover:bg-white/80'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="flex items-center">
                  <i className={`${item.icon} mr-3 text-2xl transform transition-all duration-300 ${
                    isActive(item.path) 
                      ? 'animate-bounce scale-110' 
                      : 'hover:scale-125 hover:rotate-12'
                  }`}></i>
                  {item.label}
                  {isActive(item.path) && (
                    <i className="ri-check-line ml-auto text-2xl animate-heartbeat"></i>
                  )}
                </span>
              </Link>
            ))}
            
            <Link 
              to="/appointment" 
              className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all duration-300 cursor-pointer transform hover:scale-105 overflow-hidden group shadow-lg hover:shadow-xl ${
                isActive('/appointment')
                  ? 'bg-gradient-to-r from-cyan-700 to-emerald-700 text-white scale-105'
                  : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-teal-600 hover:to-emerald-600'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="flex items-center relative z-10">
                <i className={`ri-calendar-line mr-3 text-2xl transform transition-all duration-300 ${
                  isActive('/appointment') 
                    ? 'animate-heartbeat scale-110' 
                    : 'group-hover:scale-125 group-hover:rotate-12'
                }`}></i>
                Book EV Service
                {isActive('/appointment') && (
                  <i className="ri-check-line ml-auto text-2xl animate-heartbeat"></i>
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
}
